import dayjs from "dayjs";
import { UseFormStateReturn } from "react-hook-form";
import { UtilityBillFormData } from "types";
import { UTILITY_BILL_STATUSES } from "components";
import { isNullOrUndefined } from "utils";
import { UtilityBillStatusKey } from "../../types";

interface UseUtilityBillButtonStatesParams {
  formState: UseFormStateReturn<UtilityBillFormData>;
  utilityBillStatus?: UtilityBillStatusKey;
  isProcessing: boolean;
  invalidDates: boolean;
  isCollectorEmpty: boolean;
  isManualApproveDisabled: boolean;
  initialBtnValue: any;
  watchValues: UtilityBillFormData;
  newCollectorId?: number;
  isCreating?: boolean;
  isAuditor?: boolean;
}

export const useUtilityBillButtonStates = ({
  formState,
  utilityBillStatus,
  isProcessing,
  invalidDates,
  isCollectorEmpty,
  isManualApproveDisabled,
  initialBtnValue,
  watchValues,
  newCollectorId,
  isCreating = false,
  isAuditor = false,
}: UseUtilityBillButtonStatesParams) => {
  // Common loading/processing states
  const hasFormErrors = Object.keys(formState.errors).length !== 0;
  const isFormUnchanged = !formState.isDirty && isNullOrUndefined(newCollectorId);

  // Status checks
  const isApprovedOrLocked =
    utilityBillStatus === "Approved (Signed Off)" ||
    utilityBillStatus === "Approved (Locked)";
  const isAlreadyInvalidData = utilityBillStatus === "Not Applicable (Invalid Data)";
  const isAlreadyCostOnly = utilityBillStatus === "Not Applicable (Cost Only)";

  // Form validation states for status buttons
  const hasInitialValues = initialBtnValue && !formState.isDirty;
  const isCleanedStatus = UTILITY_BILL_STATUSES.CLEANED && !formState.isDirty;
  const canSubmitForm = invalidDates || isProcessing || hasInitialValues || isCleanedStatus;
  const isAmountMissing = isNullOrUndefined(watchValues.amount);

  const isSaveButtonDisabled = invalidDates || isProcessing || isFormUnchanged || isCollectorEmpty;

  const isConfirmButtonDisabled =
    !isSaveButtonDisabled ||
    invalidDates ||
    isProcessing ||
    isManualApproveDisabled ||
    hasFormErrors;

  // Button is disabled if save is enabled, or status is approved/locked/invalid,
  // or form cannot be submitted, or there are errors
  const isNotApplicableInvalidDataButtonDisabled =
    !isSaveButtonDisabled ||
    isApprovedOrLocked ||
    isAlreadyInvalidData ||
    !canSubmitForm ||
    isManualApproveDisabled ||
    hasFormErrors;

  // Button is disabled if save is enabled, or status is approved/locked/cost only,
  // or amount is missing, or form cannot be submitted
  const isNotApplicableCostOnlyButtonDisabled =
    !isSaveButtonDisabled ||
    isApprovedOrLocked ||
    isAlreadyCostOnly ||
    isAmountMissing ||
    !canSubmitForm;

  // Validation for create mode button (Add bill)
  const isStartDateInvalid =
    isNullOrUndefined(watchValues.startDate) ||
    (watchValues.startDate && !dayjs(watchValues.startDate).isValid());
  const isEndDateInvalid =
    isNullOrUndefined(watchValues.endDate) ||
    (watchValues.endDate && !dayjs(watchValues.endDate).isValid());
  const isCreateButtonDisabled =
    isCreating ||
    isAuditor ||
    invalidDates ||
    isProcessing ||
    isCollectorEmpty ||
    isStartDateInvalid ||
    isEndDateInvalid;

  return {
    isSaveButtonDisabled,
    isConfirmButtonDisabled,
    isNotApplicableInvalidDataButtonDisabled,
    isNotApplicableCostOnlyButtonDisabled,
    isCreateButtonDisabled,
    hasFormErrors,
    isFormUnchanged,
    isApprovedOrLocked,
    isAlreadyInvalidData,
    isAlreadyCostOnly,
    canSubmitForm,
    isAmountMissing,
  };
};

